<script setup>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import MessageBubble from './MessageBubble.vue'

const props = defineProps({
  messages:   { type: Array, required: true },
  customerId: { type: Number, default: null }
})

const { t } = useI18n()

const NEAR_BOTTOM_PX = 80

const scroller = ref(null)
const newCount = ref(0)
const nearBottom = ref(true)

const isNearBottom = () => {
  const el = scroller.value
  if (!el) return true
  return el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX
}

const scrollToBottom = async () => {
  await nextTick()
  if (scroller.value) scroller.value.scrollTop = scroller.value.scrollHeight
  newCount.value = 0
  nearBottom.value = true
}

const onScroll = () => {
  nearBottom.value = isNearBottom()
  if (nearBottom.value) newCount.value = 0
}

watch(
  () => props.messages.length,
  (len, prev) => {
    if (len <= (prev ?? 0)) return
    if (nearBottom.value) {
      scrollToBottom()
    } else {
      newCount.value += len - (prev ?? 0)
    }
  },
)

onMounted(() => {
  scrollToBottom()
  scroller.value?.addEventListener('scroll', onScroll, { passive: true })
})
onBeforeUnmount(() => {
  scroller.value?.removeEventListener('scroll', onScroll)
})
</script>

<template>
  <div class="relative flex-1 min-h-0">
    <div
      ref="scroller"
      class="absolute inset-0 overflow-y-auto p-4 space-y-2 bg-[color:var(--bg)]"
    >
      <MessageBubble
        v-for="m in messages"
        :key="m.id"
        :message="m"
        :customer-id="customerId"
      />
    </div>
    <button
      v-if="newCount > 0"
      type="button"
      class="absolute bottom-3 right-3 inline-flex items-center gap-1 text-xs fw-value rounded-full px-3 py-1.5 shadow"
      :style="{ background: 'var(--accent)', color: 'var(--accentInk)' }"
      @click="scrollToBottom"
    >
      {{ t('inbox.thread.newBelow', { n: newCount }) }}
    </button>
  </div>
</template>
